/**
 * Config for Bond Creation Historic guardian forms — dropdown mapping, law-firm id, and UI keys.
 */

import {
  mapProfessionalGuardianProbate,
} from "../Bond Retrieval/hooks/bondRetrievalGuardianMappers";
import {
  defaultFieldMap,
  guardianshipFieldMap,
} from "../../../components/guardian/principalHelpers";

export const HISTORIC_GUARDIAN_BOND = Object.freeze({
  guardianship: "guardianship",
  probate: "probate",
  trustee: "trustee",
  va: "va",
});

/** Rows for `HistoricPgSection` `SelectedEntityCard` (logical keys → PG Autocomplete option fields). */
export const HISTORIC_PG_CARD_ROWS_DEFAULT = Object.freeze(["name", "address", "cell"]);

/** Historic listing uses address/cell on PG options (same shape as probate mapper). */
const mapPgOption = mapProfessionalGuardianProbate;

/**
 * Logical field keys → storage keys on each guardian row.
 * Used by HistoricGuardianFormBase for reads/writes without branching the whole form.
 */
export const HISTORIC_GUARDIAN_FIELD_KEYS = {
  [HISTORIC_GUARDIAN_BOND.guardianship]: {
    name: guardianshipFieldMap.name,
    address: guardianshipFieldMap.address,
    city: guardianshipFieldMap.city,
    state: guardianshipFieldMap.state,
    zip: guardianshipFieldMap.zip_code,
    occupation: guardianshipFieldMap.occupation,
    relationship: guardianshipFieldMap.relationship,
    citizenship: guardianshipFieldMap.citizenship,
    customCitizenship: "custom_guardian_citizenship",
    country: "country",
    customCountry: "custom_country",
    email: "email",
    ssn: "ssn",
  },
  [HISTORIC_GUARDIAN_BOND.probate]: {
    name: defaultFieldMap.name,
    address: defaultFieldMap.address,
    city: defaultFieldMap.city,
    state: defaultFieldMap.state,
    zip: defaultFieldMap.zip_code,
    occupation: defaultFieldMap.occupation,
    relationship: defaultFieldMap.relationship,
    citizenship: defaultFieldMap.citizenship,
    customCitizenship: defaultFieldMap.custom_citizenship,
    country: defaultFieldMap.country,
    customCountry: defaultFieldMap.custom_country,
    email: defaultFieldMap.email,
    ssn: defaultFieldMap.ssn,
  },
  [HISTORIC_GUARDIAN_BOND.trustee]: {
    name: defaultFieldMap.name,
    address: defaultFieldMap.address,
    city: defaultFieldMap.city,
    state: defaultFieldMap.state,
    zip: defaultFieldMap.zip_code,
    occupation: defaultFieldMap.occupation,
    relationship: defaultFieldMap.relationship,
    citizenship: defaultFieldMap.citizenship,
    customCitizenship: defaultFieldMap.custom_citizenship,
    country: defaultFieldMap.country,
    customCountry: defaultFieldMap.custom_country,
    email: defaultFieldMap.email,
    ssn: defaultFieldMap.ssn,
  },
  [HISTORIC_GUARDIAN_BOND.va]: {
    name: defaultFieldMap.name,
    address: defaultFieldMap.address,
    city: defaultFieldMap.city,
    state: defaultFieldMap.state,
    zip: defaultFieldMap.zip_code,
    occupation: defaultFieldMap.occupation,
    relationship: defaultFieldMap.relationship,
    citizenship: defaultFieldMap.citizenship,
    customCitizenship: defaultFieldMap.custom_citizenship,
    country: defaultFieldMap.country,
    customCountry: defaultFieldMap.custom_country,
    email: defaultFieldMap.email,
    ssn: defaultFieldMap.ssn,
  },
};

/** @typedef {'zip-code' | 'fields-location'} HistoricZipLookupMode */

/**
 * @param {string} role
 * @param {{ lawfirmId?: number }} lawfirm
 * @returns {number}
 */
export function getHistoricAttorneyLawFirmId(role, lawfirm) {
  let id = 0;
  if (role === "law_firm_user" || role === "law_firm_admin") {
    id = lawfirm?.lawfirmId ?? 0;
  }
  return id;
}

export const HISTORIC_GUARDIAN_CONFIG = {
  [HISTORIC_GUARDIAN_BOND.guardianship]: {
    bondType: HISTORIC_GUARDIAN_BOND.guardianship,
    /** Label for principal email TextField when shown (debounced guardian flow). */
    emailFieldLabel: "Email of Professional Guardian",
    mapProfessionalGuardian: mapPgOption,
    isGuest: false,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[HISTORIC_GUARDIAN_BOND.guardianship],
    /** @type {HistoricZipLookupMode} */
    zipLookup: "zip-code",
    guardianCountKey: "GuardianNumber",
    pgLookupBoxSx: null,
    /** @type {readonly ('name'|'address'|'cell')[]} */
    pgCardRows: [...HISTORIC_PG_CARD_ROWS_DEFAULT],
    attorneyUi: "guardianship",
    emailUi: "newPgDebounced",
    bondAmountSsnOp: "gt",
    showManualOccupation: true,
    manualOccupationWhen: "always",
    relationshipLabel: "Relationship to Ward",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: {
      name: { mt: 2, mb: 2 },
      citizenshipSelect: {},
      specifyCitizenship: {},
      countrySelect: {},
      specifyCountry: {},
    },
  },
  [HISTORIC_GUARDIAN_BOND.probate]: {
    bondType: HISTORIC_GUARDIAN_BOND.probate,
    emailFieldLabel: "Email",
    mapProfessionalGuardian: mapPgOption,
    isGuest: false,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[HISTORIC_GUARDIAN_BOND.probate],
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: null,
    pgCardRows: [...HISTORIC_PG_CARD_ROWS_DEFAULT],
    attorneyUi: "probate",
    emailUi: "newPgOrAttorneyDebounced",
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "highBondNotPgNotAttorney",
    relationshipLabel:
      "What is the principal's relationship to the deceased?",
    relationshipRequired: false,
    relationshipShowWhen: "notPgNotAttorney",
    googlePlaceholderManual: "Search Principal Address",
    manualStyles: {
      name: { mb: 2 },
      citizenshipSelect: { mb: 2 },
      specifyCitizenship: { mb: 2 },
      countrySelect: { mb: 2 },
      specifyCountry: { mb: 2 },
    },
  },
  [HISTORIC_GUARDIAN_BOND.trustee]: {
    bondType: HISTORIC_GUARDIAN_BOND.trustee,
    emailFieldLabel: "Email",
    mapProfessionalGuardian: mapPgOption,
    isGuest: false,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[HISTORIC_GUARDIAN_BOND.trustee],
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: { mt: 4 },
    pgCardRows: [...HISTORIC_PG_CARD_ROWS_DEFAULT],
    attorneyUi: "trustee",
    emailUi: "newPgPlain",
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "always",
    relationshipLabel: "Relationship to Beneficiary",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: {
      name: { mt: 2, mb: 2 },
      citizenshipSelect: {},
      specifyCitizenship: {},
      countrySelect: {},
      specifyCountry: {},
    },
  },
  [HISTORIC_GUARDIAN_BOND.va]: {
    bondType: HISTORIC_GUARDIAN_BOND.va,
    emailFieldLabel: "Email",
    mapProfessionalGuardian: mapPgOption,
    isGuest: false,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[HISTORIC_GUARDIAN_BOND.va],
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: { mt: 4 },
    pgCardRows: [...HISTORIC_PG_CARD_ROWS_DEFAULT],
    attorneyUi: "va",
    emailUi: "newPgPlain",
    bondAmountSsnOp: "gt",
    showManualOccupation: false,
    manualOccupationWhen: "never",
    relationshipLabel: "Relationship to Beneficiary",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: {
      name: { mt: 2, mb: 2 },
      citizenshipSelect: {},
      specifyCitizenship: {},
      countrySelect: {},
      specifyCountry: {},
    },
  },
};
